/**
 * Research Chambers (Phase 2C): six small, focused test spaces, one per
 * movement question, side by side along +X and joined by a walkway at z ≈ 30.
 * Each chamber has a waypoint pad at its south edge facing -Z (F2 panel or
 * `?level=chambers`).
 *
 *  1 Open arena          free speed / turning / jump-length measurement
 *  2 Curved geometry     convex crests (r = 3, 5, 8 m), quarter-pipe, bend bowl
 *  3 Momentum transfer   down→up slope conversion, rebound lane, drop onto a slope
 *  4 Vertical traversal  1 m staircase, a 1.6 m gate, compression shaft, coupling column
 *  5 Precision corridor  narrowing safe path over a lethal floor (timed: finish)
 *  6 Combination         descent → bend pillar → coupled channel over lethal floor → rebound ledge
 *
 * Sizes follow the default tuning (apex v²/2g = 1.31 m, running jump ≈ 5.9 m)
 * so that each chamber states what the baseline can and cannot do.
 */
import type { LevelData, LightDef, SolidDef, SpawnPoint, TriggerDef } from '../world/LevelData';
import { arcWall, concaveCurve, ramp, tiledBox } from '../world/LevelBuilders';

const SPACING = 70;
const cx = (i: number): number => i * SPACING;

const box = (id: string, c: [number, number, number], half: [number, number, number], material: string, rotationDeg?: [number, number, number]): SolidDef => ({
  id,
  shape: { type: 'box', halfExtents: half },
  position: c,
  material,
  ...(rotationDeg ? { rotationDeg } : {}),
});

const cyl = (id: string, x: number, yBase: number, z: number, radius: number, height: number, material: string): SolidDef => ({
  id,
  shape: { type: 'cylinder', radius, halfHeight: height / 2 },
  position: [x, yBase + height / 2, z],
  material,
});

/** Horizontal cylinder along X whose top is at `top`: a convex crest of radius r. */
const crest = (id: string, x: number, z: number, r: number, top: number, width: number, material: string): SolidDef => ({
  id,
  shape: { type: 'cylinder', radius: r, halfHeight: width / 2 },
  position: [x, top - r, z],
  rotationDeg: [0, 0, 90],
  material,
});

const floor = (i: number, material = 'safe.stone', top = 0): SolidDef[] => tiledBox(`c${i + 1}.floor`, [cx(i), top - 0.5, -10], [20, 0.5, 35], material);
const pad = (i: number): SpawnPoint => ({ position: [cx(i), 0.02, 21], yawDeg: 0 });

// 1 · Open arena -------------------------------------------------------------
const arena: SolidDef[] = [
  ...floor(0),
  ...ramp('c1.kickLow', [cx(0) - 10, 0, -8], [cx(0) - 10, 1.5, -16], 6, 0.6, 'safe.marble'),
  box('c1.kickLowTop', [cx(0) - 10, 1.2, -17], [3, 0.3, 1], 'safe.marble'),
  ...ramp('c1.kickHigh', [cx(0) + 10, 0, -8], [cx(0) + 10, 2.6, -15], 6, 0.6, 'safe.marble'),
  box('c1.kickHighTop', [cx(0) + 10, 2.3, -16], [3, 0.3, 1], 'safe.marble'),
];

// 2 · Curved geometry -----------------------------------------------------------
const curved: SolidDef[] = [
  ...floor(1),
  crest('c2.crest3', cx(1) + 9, 8, 3, 1, 8, 'safe.stone'),
  crest('c2.crest5', cx(1) + 9, -4, 5, 1, 8, 'safe.stone'),
  crest('c2.crest8', cx(1) + 9, -19, 8, 1, 8, 'safe.stone'),
  // Quarter-pipe on the west lane: walkable up to 48°, wall beyond.
  ...concaveCurve('c2.pipe', [cx(1) - 9, 6, -26], 6, 0, 80, 16, 8, 0.4, 'safe.marble'),
  // Bend bowl: 300° ring of bend surface, entry gap facing +Z.
  ...arcWall('c2.bowl', [cx(1) - 6, 0, 2], 6, 300, 600, 20, 3, 0.4, 'reactive.bend'),
];

// 3 · Momentum transfer --------------------------------------------------------
const momentum: SolidDef[] = [
  ...floor(2),
  // West lane: 6 m drop over 24 m (14°), 8 m flat, then 3 m climb over 12 m.
  box('c3.dropTop', [cx(2) - 9, 5.5, 22], [3, 0.5, 2], 'safe.stone'),
  ...ramp('c3.drop', [cx(2) - 9, 6, 20], [cx(2) - 9, 0, -4], 6, 0.6, 'safe.marble'),
  ...ramp('c3.climb', [cx(2) - 9, 0, -12], [cx(2) - 9, 3, -24], 6, 0.6, 'safe.marble'),
  box('c3.climbTop', [cx(2) - 9, 2.5, -28], [3, 0.5, 4], 'safe.stone'),
  // East lane: rebound wall (compression) and a 45° deflector.
  box('c3.rebound', [cx(2) + 9, 2.5, -40], [4, 2.5, 0.5], 'reactive.compression'),
  box('c3.deflect', [cx(2) + 3, 2.5, -30], [0.4, 2.5, 3], 'reactive.compression', [0, 45, 0]),
  // Centre: up a 16° ramp onto a 2 m ledge, then drop onto a 14° downslope (landing on a slope keeps speed).
  ...ramp('c3.up', [cx(2), 0, 20], [cx(2), 2, 13], 5, 0.6, 'safe.marble'),
  box('c3.ledge', [cx(2), 1.5, 9.5], [2.5, 0.5, 3.5], 'safe.stone'),
  ...ramp('c3.landing', [cx(2), 1.4, 2], [cx(2), 0, -4], 5, 0.4, 'safe.marble'),
];

// 4 · Vertical traversal -------------------------------------------------------
const step = (n: number, z: number, top: number): SolidDef => box(`c4.step${n}`, [cx(3) - 10, top / 2, z], [2.5, top / 2, 2], 'safe.stone');
const vertical: SolidDef[] = [
  ...floor(3),
  // 1.0 m steps: each reachable with a baseline jump (apex 1.31 m).
  step(1, 10, 1),
  step(2, 6, 2),
  step(3, 2, 3),
  step(4, -2, 4),
  // 1.6 m gate above step 4: beyond the baseline apex.
  step(5, -6, 5.6),
  // Compression shaft: facing walls 3 m apart, open to the south.
  box('c4.shaftW', [cx(3) + 5.25, 5, -6], [0.25, 5, 4], 'reactive.compression'),
  box('c4.shaftE', [cx(3) + 8.75, 5, -6], [0.25, 5, 4], 'reactive.compression'),
  box('c4.shaftTop', [cx(3) + 7, 7.75, -12.5], [3, 0.25, 2.5], 'safe.stone'),
  // Coupling column.
  cyl('c4.column', cx(3) + 7, 0, -26, 2, 10, 'reactive.coupling'),
];

// 5 · Precision hazard corridor ---------------------------------------------------
const hazard: SolidDef[] = [
  // Lethal floor 3 m below the path.
  ...tiledBox('c5.pit', [cx(4), -3.5, -10], [20, 0.5, 35], 'lethal.basic'),
  box('c5.a', [cx(4), -0.5, 15], [1.5, 0.5, 10], 'safe.stone'), // 3 m wide, z 25..5
  box('c5.b', [cx(4), -0.5, 0], [1, 0.5, 5], 'safe.stone'), // 2 m wide, z 5..-5
  box('c5.bWallL', [cx(4) - 1.25, 0.6, 0], [0.25, 0.6, 5], 'lethal.basic'),
  box('c5.bWallR', [cx(4) + 1.25, 0.6, 0], [0.25, 0.6, 5], 'lethal.basic'),
  // Stepping islands with 2.5 m gaps.
  box('c5.i1', [cx(4), -0.5, -8.25], [0.75, 0.5, 0.75], 'safe.stone'),
  box('c5.i2', [cx(4) + 1, -0.5, -12.5], [0.75, 0.5, 0.75], 'safe.stone'),
  box('c5.i3', [cx(4) - 0.5, -0.5, -16.75], [0.75, 0.5, 0.75], 'safe.stone'),
  box('c5.i4', [cx(4), -0.5, -21], [0.75, 0.5, 0.75], 'safe.stone'),
  // 1.2 m strip with two low lethal bars to hop.
  box('c5.c', [cx(4), -0.5, -28], [0.6, 0.5, 5], 'safe.stone'),
  box('c5.bar1', [cx(4), 0.25, -26], [0.6, 0.25, 0.15], 'lethal.basic'),
  box('c5.bar2', [cx(4), 0.25, -30], [0.6, 0.25, 0.15], 'lethal.basic'),
  box('c5.end', [cx(4), -0.5, -38], [3, 0.5, 5], 'safe.stone'),
];

// 6 · Combination ------------------------------------------------------------------
const combo: SolidDef[] = [
  // Floor with a gap for the lethal channel (x cx+3..cx+17, z -14..-20).
  ...tiledBox('c6.floorS', [cx(5), -0.5, 6], [20, 0.5, 19], 'safe.stone'), // z 25..-13
  ...tiledBox('c6.floorW', [cx(5) - 8.5, -0.5, -16.5], [11.5, 0.5, 3.5], 'safe.stone'), // x -20..3
  box('c6.floorE', [cx(5) + 18.5, -0.5, -16.5], [1.5, 0.5, 3.5], 'safe.stone'), // x 17..20
  ...tiledBox('c6.floorN', [cx(5), -0.5, -32.5], [20, 0.5, 12.5], 'safe.stone'), // z -20..-45
  ...tiledBox('c6.channel', [cx(5) + 10, -3.5, -16.5], [7, 0.5, 3.5], 'lethal.basic'),
  // Descent from a 5 m platform.
  box('c6.top', [cx(5) - 6, 4.5, 21], [3, 0.5, 2], 'safe.stone'),
  ...ramp('c6.descent', [cx(5) - 6, 5, 19], [cx(5) - 6, 0, 1], 6, 0.6, 'safe.marble'),
  // Bend pillar turning the line east.
  cyl('c6.bend', cx(5) - 4, 0, -6, 1.2, 6, 'reactive.bend'),
  // Coupling wall along the channel's north edge.
  box('c6.couple', [cx(5) + 10, 1, -20.25], [7, 4, 0.25], 'reactive.coupling'),
  // Rebound wall and the 2.5 m ledge (above the baseline apex) beside it.
  box('c6.rebound', [cx(5) + 19.75, 2.5, -8], [0.25, 2.5, 4], 'reactive.compression'),
  box('c6.ledge', [cx(5) + 17, 1.25, -1], [3, 1.25, 3], 'safe.greenstone'),
  // Safe detour: a 1 m step up and over.
  box('c6.stepA', [cx(5) + 12, 0.5, -1], [2, 0.5, 3], 'safe.stone'),
];

const walkway = tiledBox('walkway', [cx(2.5), -0.5, 30], [cx(2.5) + 20, 0.5, 5], 'safe.stone');

const triggers: TriggerDef[] = [
  { id: 'start', kind: 'start', center: [cx(4), 1, 22], halfExtents: [1.5, 2, 1], material: 'trigger.start' },
  { id: 'finish', kind: 'finish', center: [cx(4), 1.5, -40], halfExtents: [3, 2, 0.5], material: 'trigger.finish' },
];

const lantern = (x: number, y: number, z: number): LightDef => ({ position: [x, y, z], color: 0xffa64d, intensity: 24, distance: 30 });

const NAMES = ['Open arena', 'Curved geometry', 'Momentum transfer', 'Vertical traversal', 'Precision corridor', 'Combination'];

export const RESEARCH_CHAMBERS: LevelData = {
  id: 'research-chambers',
  name: 'Research Chambers',
  version: 1,
  solids: [...walkway, ...arena, ...curved, ...momentum, ...vertical, ...hazard, ...combo],
  triggers,
  start: pad(0),
  killPlaneY: -30,
  lights: NAMES.flatMap((_, i) => [lantern(cx(i) - 10, 6, 12), lantern(cx(i) + 10, 6, -4), lantern(cx(i) - 6, 6, -28)]),
  labels: NAMES.map((n, i) => ({ text: `${i + 1} · ${n.toUpperCase()}`, position: [cx(i), 7, 24] as [number, number, number] })),
  waypoints: NAMES.map((name, i) => ({ name: `${i + 1} ${name}`, spawn: pad(i) })),
};
